import React, { useState, useEffect, useRef } from 'react';
import { countries, Country } from '../../../utils/countries';

interface CountrySelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (countryName: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
}

const CountrySelect: React.FC<CountrySelectProps> = ({
  id,
  name,
  value,
  onChange,
  placeholder = "Select a country...",
  className = "",
  error,
  disabled = false,
  ariaDescribedBy,
  ariaInvalid,
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(value || '');
    const country = countries.find(c => c.name === value);
    setSelectedCountry(country || null);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filterCountries = (query: string) => {
    if (!query) {
      setFilteredCountries(countries);
      return;
    }

    const filtered = countries.filter(country =>
      country.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    filterCountries(value);
    setShowDropdown(true);
  };

  const handleCountrySelect = (country: Country) => {
    setSearchTerm(country.name);
    setSelectedCountry(country);
    onChange(country.name);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    filterCountries(searchTerm);
    setShowDropdown(true);
  };

  const handleBlur = () => {
    const exactMatch = countries.find(
      c => c.name.toLowerCase() === searchTerm.toLowerCase()
    );
    if (exactMatch) {
      setSearchTerm(exactMatch.name);
      setSelectedCountry(exactMatch);
      onChange(exactMatch.name);
    } else {
      const fallback = selectedCountry?.name ?? '';
      setSearchTerm(fallback);
      setSelectedCountry(selectedCountry);
      onChange(fallback);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {selectedCountry && (
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl pointer-events-none">
            {selectedCountry.flag}
          </span>
        )}
        <input
          id={id}
          name={name}
          type="text"
          disabled={disabled}
          className={`w-full ${selectedCountry ? 'pl-12' : 'pl-3'} pr-3 py-2 text-sm md:text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
        />
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {!disabled && showDropdown && filteredCountries.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto border border-gray-200"
        >
          <ul className="py-1">
            {filteredCountries.map((country) => (
              <li
                key={country.code}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm md:text-base flex items-center"
                onClick={() => handleCountrySelect(country)}
              >
                <span className="text-xl mr-2">{country.flag}</span>
                <span>{country.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!disabled && showDropdown && filteredCountries.length === 0 && searchTerm && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200"
        >
          <div className="px-3 py-2 text-sm text-gray-500">
            No countries found
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
